import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/hooks/use-toast';
import { Plus, Edit, Trash2, Eye, EyeOff, Rocket, AlertTriangle, Info, CheckCircle, XCircle, Wrench, Languages, Loader2 } from 'lucide-react';

interface SystemBanner {
  id: string;
  title: string;
  message_fr: string;
  message_en: string;
  banner_type: 'info' | 'warning' | 'success' | 'error' | 'maintenance';
  background_color: string;
  icon: string;
  is_active: boolean;
  is_dismissible: boolean;
  priority: number;
  start_date: string;
  end_date: string | null;
  created_at: string;
}

const BANNER_TYPES = [
  { value: 'info', label: 'Information', icon: Info, color: 'blue' },
  { value: 'warning', label: 'Avertissement', icon: AlertTriangle, color: 'yellow' },
  { value: 'success', label: 'Succès', icon: CheckCircle, color: 'green' },
  { value: 'error', label: 'Erreur', icon: XCircle, color: 'red' },
  { value: 'maintenance', label: 'Maintenance', icon: Wrench, color: 'orange' },
];

const GRADIENT_OPTIONS = [
  { value: 'from-orange-500 via-orange-600 to-amber-500', label: 'Orange/Amber (Lancement)' },
  { value: 'from-blue-500 via-blue-600 to-indigo-500', label: 'Bleu/Indigo (Info)' },
  { value: 'from-yellow-400 via-yellow-500 to-orange-500', label: 'Jaune/Orange (Avertissement)' },
  { value: 'from-green-500 via-green-600 to-emerald-500', label: 'Vert/Émeraude (Succès)' },
  { value: 'from-red-500 via-red-600 to-rose-500', label: 'Rouge/Rose (Erreur)' },
  { value: 'from-purple-500 via-purple-600 to-violet-500', label: 'Violet (Maintenance)' },
  { value: 'from-gray-700 via-gray-800 to-gray-900', label: 'Gris foncé (Neutre)' },
];

const ICON_OPTIONS = [
  { value: 'rocket', label: '🚀 Rocket' },
  { value: 'info', label: 'ℹ️ Info' },
  { value: 'warning', label: '⚠️ Warning' },
  { value: 'success', label: '✅ Success' },
  { value: 'error', label: '❌ Error' },
  { value: 'maintenance', label: '🔧 Maintenance' },
  { value: 'megaphone', label: '📢 Megaphone' },
  { value: 'gift', label: '🎁 Gift' },
  { value: 'star', label: '⭐ Star' },
];

export const SystemBannersManager = () => {
  const [banners, setBanners] = useState<SystemBanner[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingBanner, setEditingBanner] = useState<SystemBanner | null>(null);
  const [isTranslating, setIsTranslating] = useState(false);
  const { toast } = useToast();

  // Fonction de traduction automatique FR → EN
  const translateToEnglish = async () => {
    if (!formData.message_fr.trim()) {
      toast({
        title: 'Erreur',
        description: 'Veuillez d\'abord saisir le message en français',
        variant: 'destructive',
      });
      return;
    }

    setIsTranslating(true);
    try {
      // Utiliser l'edge function translate-product avec un ID fictif
      const { data, error } = await supabase.functions.invoke('translate-product', {
        body: {
          productId: 'banner-translation',
          title: formData.message_fr,
          description: '',
          targetLanguage: 'en',
        },
      });

      if (error) throw error;

      if (data?.title) {
        setFormData({ ...formData, message_en: data.title });
        toast({
          title: 'Succès',
          description: 'Traduction automatique appliquée',
        });
      } else {
        throw new Error('Pas de traduction retournée');
      }
    } catch (error: any) {
      console.error('Translation error:', error);
      toast({
        title: 'Erreur de traduction',
        description: 'La traduction automatique a échoué. Veuillez saisir manuellement.',
        variant: 'destructive',
      });
    } finally {
      setIsTranslating(false);
    }
  };

  const [formData, setFormData] = useState({
    title: '',
    message_fr: '',
    message_en: '',
    banner_type: 'info' as SystemBanner['banner_type'],
    background_color: 'from-orange-500 via-orange-600 to-amber-500',
    icon: 'rocket',
    is_active: true,
    is_dismissible: true,
    priority: 0,
    start_date: new Date().toISOString().slice(0, 16),
    end_date: '',
  });

  useEffect(() => {
    fetchBanners();
  }, []);

  const fetchBanners = async () => {
    try {
      // Table system_banners - requires CREATE_SYSTEM_BANNERS.sql to be executed first
      const { data, error } = await (supabase.from as any)('system_banners')
        .select('*')
        .order('priority', { ascending: false })
        .order('created_at', { ascending: false });

      if (error) throw error;
      setBanners(data || []);
    } catch (error: any) {
      toast({
        title: 'Erreur',
        description: 'Impossible de charger les bannières',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      message_fr: '',
      message_en: '',
      banner_type: 'info',
      background_color: 'from-orange-500 via-orange-600 to-amber-500',
      icon: 'rocket',
      is_active: true,
      is_dismissible: true,
      priority: 0,
      start_date: new Date().toISOString().slice(0, 16),
      end_date: '',
    });
    setEditingBanner(null);
  };

  const handleEdit = (banner: SystemBanner) => {
    setEditingBanner(banner);
    setFormData({
      title: banner.title,
      message_fr: banner.message_fr,
      message_en: banner.message_en,
      banner_type: banner.banner_type,
      background_color: banner.background_color,
      icon: banner.icon,
      is_active: banner.is_active,
      is_dismissible: banner.is_dismissible,
      priority: banner.priority,
      start_date: banner.start_date ? new Date(banner.start_date).toISOString().slice(0, 16) : '',
      end_date: banner.end_date ? new Date(banner.end_date).toISOString().slice(0, 16) : '',
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    try {
      const payload = {
        title: formData.title,
        message_fr: formData.message_fr,
        message_en: formData.message_en,
        banner_type: formData.banner_type,
        background_color: formData.background_color,
        icon: formData.icon,
        is_active: formData.is_active,
        is_dismissible: formData.is_dismissible,
        priority: formData.priority,
        start_date: formData.start_date ? new Date(formData.start_date).toISOString() : null,
        end_date: formData.end_date ? new Date(formData.end_date).toISOString() : null,
      };

      if (editingBanner) {
        const { error } = await (supabase.from as any)('system_banners')
          .update(payload)
          .eq('id', editingBanner.id);
        
        if (error) throw error;
        toast({ title: 'Succès', description: 'Bannière mise à jour' });
      } else {
        const { error } = await (supabase.from as any)('system_banners')
          .insert(payload);
        
        if (error) throw error;
        toast({ title: 'Succès', description: 'Bannière créée' });
      }

      setDialogOpen(false);
      resetForm();
      fetchBanners();
    } catch (error: any) {
      toast({
        title: 'Erreur',
        description: error.message,
        variant: 'destructive',
      });
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Supprimer cette bannière ?')) return;

    try {
      const { error } = await (supabase.from as any)('system_banners')
        .delete()
        .eq('id', id);

      if (error) throw error;
      toast({ title: 'Succès', description: 'Bannière supprimée' });
      fetchBanners();
    } catch (error: any) {
      toast({
        title: 'Erreur',
        description: error.message,
        variant: 'destructive',
      });
    }
  };

  const toggleActive = async (banner: SystemBanner) => {
    try {
      const { error } = await (supabase.from as any)('system_banners')
        .update({ is_active: !banner.is_active })
        .eq('id', banner.id);

      if (error) throw error;
      toast({ 
        title: 'Succès', 
        description: banner.is_active ? 'Bannière désactivée' : 'Bannière activée' 
      });
      fetchBanners();
    } catch (error: any) {
      toast({
        title: 'Erreur',
        description: error.message,
        variant: 'destructive',
      });
    }
  };

  const getTypeIcon = (type: string) => {
    const typeConfig = BANNER_TYPES.find(t => t.value === type);
    if (!typeConfig) return <Info className="h-4 w-4" />;
    const IconComponent = typeConfig.icon;
    return <IconComponent className="h-4 w-4" />;
  };

  const getTypeBadgeColor = (type: string) => {
    switch (type) {
      case 'info': return 'bg-blue-100 text-blue-800';
      case 'warning': return 'bg-yellow-100 text-yellow-800';
      case 'success': return 'bg-green-100 text-green-800';
      case 'error': return 'bg-red-100 text-red-800';
      case 'maintenance': return 'bg-orange-100 text-orange-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex justify-between items-center">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Rocket className="h-5 w-5" />
              Gestion des Bannières Système
            </CardTitle>
            <CardDescription>
              Créez et gérez les messages d'annonce, maintenance, et notifications système
            </CardDescription>
          </div>
          <Dialog open={dialogOpen} onOpenChange={(open) => {
            setDialogOpen(open);
            if (!open) resetForm();
          }}>
            <DialogTrigger asChild>
              <Button>
                <Plus className="h-4 w-4 mr-2" />
                Nouvelle bannière
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>
                  {editingBanner ? 'Modifier la bannière' : 'Nouvelle bannière'}
                </DialogTitle>
                <DialogDescription>
                  Configurez le message qui sera affiché à tous les utilisateurs
                </DialogDescription>
              </DialogHeader>

              <div className="grid gap-4 py-4">
                <div className="grid gap-2">
                  <Label htmlFor="title">Titre (interne)</Label>
                  <Input
                    id="title"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    placeholder="Ex: Maintenance du 15 février"
                  />
                </div>

                <div className="grid gap-2">
                  <Label htmlFor="message_fr">Message (Français)</Label>
                  <Textarea
                    id="message_fr"
                    value={formData.message_fr}
                    onChange={(e) => setFormData({ ...formData, message_fr: e.target.value })}
                    placeholder="Le message qui sera affiché en français..."
                    rows={3}
                  />
                </div>

                <div className="grid gap-2">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="message_en">Message (Anglais)</Label>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={translateToEnglish}
                      disabled={isTranslating || !formData.message_fr.trim()}
                      className="flex items-center gap-1 text-xs"
                    >
                      {isTranslating ? (
                        <Loader2 className="h-3 w-3 animate-spin" />
                      ) : (
                        <Languages className="h-3 w-3" />
                      )}
                      Traduire auto
                    </Button>
                  </div>
                  <Textarea
                    id="message_en"
                    value={formData.message_en}
                    onChange={(e) => setFormData({ ...formData, message_en: e.target.value })}
                    placeholder="The message that will be displayed in English..."
                    rows={3}
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="grid gap-2">
                    <Label>Type</Label>
                    <Select
                      value={formData.banner_type}
                      onValueChange={(value: SystemBanner['banner_type']) => 
                        setFormData({ ...formData, banner_type: value })
                      }
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {BANNER_TYPES.map((type) => (
                          <SelectItem key={type.value} value={type.value}>
                            <div className="flex items-center gap-2">
                              <type.icon className="h-4 w-4" />
                              {type.label}
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="grid gap-2">
                    <Label>Icône</Label>
                    <Select
                      value={formData.icon}
                      onValueChange={(value) => setFormData({ ...formData, icon: value })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {ICON_OPTIONS.map((icon) => (
                          <SelectItem key={icon.value} value={icon.value}>
                            {icon.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="grid gap-2">
                  <Label>Couleur de fond (gradient)</Label>
                  <Select
                    value={formData.background_color}
                    onValueChange={(value) => setFormData({ ...formData, background_color: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {GRADIENT_OPTIONS.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          <div className="flex items-center gap-2">
                            <div className={`w-6 h-4 rounded bg-gradient-to-r ${option.value}`} />
                            {option.label}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Preview */}
                <div className="grid gap-2">
                  <Label>Aperçu</Label>
                  <div className={`bg-gradient-to-r ${formData.background_color} text-white px-4 py-2 rounded-lg`}>
                    <div className="flex items-center justify-center gap-2 text-sm font-medium">
                      {formData.message_fr || 'Votre message ici...'}
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="grid gap-2">
                    <Label htmlFor="start_date">Date de début</Label>
                    <Input
                      id="start_date"
                      type="datetime-local"
                      value={formData.start_date}
                      onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                    />
                  </div>

                  <div className="grid gap-2">
                    <Label htmlFor="end_date">Date de fin (optionnelle)</Label>
                    <Input
                      id="end_date"
                      type="datetime-local"
                      value={formData.end_date}
                      onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                    />
                  </div>
                </div>

                <div className="grid gap-2">
                  <Label htmlFor="priority">Priorité (plus élevé = affiché en premier)</Label>
                  <Input
                    id="priority"
                    type="number"
                    value={formData.priority}
                    onChange={(e) => setFormData({ ...formData, priority: parseInt(e.target.value) || 0 })}
                  />
                </div>

                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={formData.is_active}
                      onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
                    />
                    <Label>Activer immédiatement</Label>
                  </div>

                  <div className="flex items-center gap-2">
                    <Switch
                      checked={formData.is_dismissible}
                      onCheckedChange={(checked) => setFormData({ ...formData, is_dismissible: checked })}
                    />
                    <Label>Peut être fermé par l'utilisateur</Label>
                  </div>
                </div>
              </div>

              <DialogFooter>
                <Button variant="outline" onClick={() => setDialogOpen(false)}>
                  Annuler
                </Button>
                <Button onClick={handleSave} disabled={!formData.title || !formData.message_fr}>
                  {editingBanner ? 'Mettre à jour' : 'Créer'}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </CardHeader>

      <CardContent>
        {loading ? (
          <div className="text-center py-8">Chargement...</div>
        ) : banners.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            Aucune bannière configurée. Cliquez sur "Nouvelle bannière" pour en créer une.
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Statut</TableHead>
                <TableHead>Titre</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Message (FR)</TableHead>
                <TableHead>Priorité</TableHead>
                <TableHead>Période</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {banners.map((banner) => (
                <TableRow key={banner.id}>
                  <TableCell>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => toggleActive(banner)}
                    >
                      {banner.is_active ? (
                        <Eye className="h-4 w-4 text-green-600" />
                      ) : (
                        <EyeOff className="h-4 w-4 text-gray-400" />
                      )}
                    </Button>
                  </TableCell>
                  <TableCell className="font-medium">{banner.title}</TableCell>
                  <TableCell>
                    <Badge className={getTypeBadgeColor(banner.banner_type)}>
                      <span className="flex items-center gap-1">
                        {getTypeIcon(banner.banner_type)}
                        {BANNER_TYPES.find(t => t.value === banner.banner_type)?.label}
                      </span>
                    </Badge>
                  </TableCell>
                  <TableCell className="max-w-xs truncate">
                    {banner.message_fr}
                  </TableCell>
                  <TableCell>{banner.priority}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {new Date(banner.start_date).toLocaleDateString('fr-FR')}
                    {banner.end_date && (
                      <> → {new Date(banner.end_date).toLocaleDateString('fr-FR')}</>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleEdit(banner)}
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDelete(banner.id)}
                        className="text-red-600 hover:text-red-700"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
};
